import Framework from '@greenville/framework';
import { applySnapshot, types } from 'mobx-state-tree';
import * as constants from '../../../common/constants';

const questionObj = types.model('QuestionOption', {
  id: types.maybeNull(types.integer),
  key: types.maybeNull(types.string),
  option_text: types.maybeNull(types.string),
  correct_choice: types.maybeNull(types.string),
  misconception_tag: types.maybeNull(types.string),
  // Keep for backward compatibility with old schema
  explanatory: types.maybeNull(types.string),
  corrective: types.maybeNull(types.string)
});

const QuestionResponse = types.model('QuestionResponse', {
  question_text: types.maybeNull(types.string),
  bloom_level: types.maybeNull(types.string),
  correct_key: types.maybeNull(types.string),
  evidence_ref: types.maybeNull(types.optional(types.array(types.union(types.string, types.number)), [])),
  evidence_quotes: types.maybeNull(types.optional(types.map(types.string), {})),
  options: types.maybeNull(types.optional(types.array(questionObj), [])),
  // Feedback object mapping answer keys (A, B, C, D) to explanatory/corrective feedback
  feedback: types.maybeNull(types.frozen())
});

const feedbackObj = types.model('FeedbackObject', {
  comment: types.maybeNull(types.string),
  reason: types.maybeNull(types.optional(types.array(types.string), [])),
  addedBy: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string)
});

const aetQuizData = types.model('aetQuizData', {
  topic_id: types.maybeNull(types.string),
  pageId: types.maybeNull(types.string),
  bookId: types.maybeNull(types.string),
  lifeCycleStatus: types.maybeNull(types.string),
  productModel: types.maybeNull(types.string),
  question_id: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  sourceURI: types.maybeNull(types.string),
  tenantId: types.maybeNull(types.string),
  topic_title: types.maybeNull(types.string),
  version: types.maybeNull(types.string),
  pageQuestionSequence: types.maybeNull(types.union(types.string, types.integer)),
  pageSequence: types.maybeNull(types.union(types.string, types.integer)),
  question: types.maybeNull(QuestionResponse, {}),
  feedbacks: types.maybeNull(types.optional(types.array(feedbackObj), [])),
  // Keep for backward compatibility
  modifiedBy: types.maybeNull(types.string)
});

const AetQuiz = types
  .model('AetQuiz', {
    data: types.optional(types.array(aetQuizData), []),
    rawResponse: types.maybeNull(types.frozen()),
    commentPlaceholder: types.optional(types.string, ''),
    commentPlaceholderFetched: types.optional(types.boolean, false)
  })
  .actions((self) => ({
    fetchAetQuiz(data) {
      const payload = {
        ...data,
        aetquiz: true
      };
      Framework.getEventManager().publish(constants.GET_CHATGPT_TOC_DATA, {
        payload
      });
    },
    addAETComment(data) {
      const payload = {
        ...data,
        aetquiz: true
      };
      Framework.getEventManager().publish(constants.ADD_MCQ_COMMENT, {
        payload
      });
    },
    setMcqCommentSuccess() {
      Framework.getEventManager().publish(constants.SET_MCQ_COMMENT_SUCCESS);
    },
    setResponse(response) {
      // Preserve commentPlaceholder and fetch flag before applying snapshot
      const currentPlaceholder = self.commentPlaceholder;
      const wasFetched = self.commentPlaceholderFetched;
      applySnapshot(self, response);
      // Restore commentPlaceholder and fetch flag after applying snapshot
      if (currentPlaceholder) {
        // eslint-disable-next-line no-param-reassign
        self.commentPlaceholder = currentPlaceholder;
        // eslint-disable-next-line no-param-reassign
        self.commentPlaceholderFetched = wasFetched;
      }
      Framework.getEventManager().publish(constants.SET_AET_TOC_DATA);
    },
    resetStoreValues() {
      const initialState = [];
      applySnapshot(self.data, initialState);
      Framework.getEventManager().publish(constants.SET_AET_TOC_DATA);
    },
    addAetQuestion(payload) {
      Framework.getEventManager().publish(constants.ADD_AET_QUESTION_DATA, {
        payload
      });
    },
    setAddAetQuestionResponse(response) {
      Framework.getEventManager().publish(constants.SET_ADD_AET_QUESTION_DATA, {
        response
      });
    },
    updateAetQuestion(payload) {
      Framework.getEventManager().publish(constants.UPDATE_AET_QUESTION_DATA, {
        payload
      });
    },
    setUpdateAetQuestionResponse(response) {
      Framework.getEventManager().publish(constants.SET_UPDATE_AET_QUESTION_DATA, {
        response
      });
    },
    deleteAetQuestion(payload) {
      Framework.getEventManager().publish(constants.DELETE_AET_QUESTION_DATA, {
        payload
      });
    },
    setDeleteAetQuestionResponse(response) {
      Framework.getEventManager().publish(constants.SET_DELETE_AET_QUESTION_DATA, {
        response
      });
    },
    fetchCommentPlaceholder(tenantId) {
      // Only fetch if not already fetched
      if (!self.commentPlaceholderFetched) {
        Framework.getEventManager().publish(constants.FETCH_AET_QUIZ_PLACEHOLDER, {
          payload: { tenantId }
        });
      }
    },
    setCommentPlaceholder(placeholderText) {
      // eslint-disable-next-line no-param-reassign
      self.commentPlaceholder = placeholderText;
      // eslint-disable-next-line no-param-reassign
      self.commentPlaceholderFetched = true;
      Framework.getEventManager().publish(constants.SET_AET_QUIZ_PLACEHOLDER);
    }
  }));

export default AetQuiz;
